import "./App.css";
import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import SponsorshipPage from "./pages/SponsorshipPage";
import SpeakersPage from "./pages/SpeakersPage";
import ProgramPage from "./pages/ProgramPage";
import GolfOutingPage from "./pages/GolfOutingPage";
import SpeakerInvitePage from "./pages/SpeakerInvitePage";
import ComingSoonPage from "./pages/ComingSoonPage";
import AdminBar from "./components/AdminBar";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminSpeakers from "./pages/AdminSpeakers";
import AdminContent from "./pages/AdminContent";
import AdminGlobalContent from "./pages/AdminGlobalContent";
import AdminSponsorship from "./pages/AdminSponsorship";
import AdminProgram from "./pages/AdminProgram";
import AdminGolf from "./pages/AdminGolf";
import AdminHomepage from "./pages/AdminHomepage";
import AdminLayoutBuilder from "./pages/AdminLayoutBuilder";
import AdminSpeakerInvite from "./pages/AdminSpeakerInvite";
import AdminSettings from "./pages/AdminSettings";
import AdminVisibility from "./pages/AdminVisibility";
import AdminComingSoon from "./pages/AdminComingSoon";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminSuppression from "./pages/AdminSuppression";
import AdminAssistantPage from "./pages/AdminAssistantPage";
import EmailCampaigns from "./pages/EmailCampaigns";
import EmailEditor from "./pages/EmailEditor";
import EmailReview from "./pages/EmailReview";
import EmailLogs from "./pages/EmailLogs";
import EmailCampaignAnalytics from "./pages/EmailCampaignAnalytics";
import EmailContactLists from "./pages/EmailContactLists";
import AdminLinkHub from "./pages/AdminLinkHub";
import AdminLinkHubAnalytics from "./pages/AdminLinkHubAnalytics";
import LinkHub from "./pages/LinkHub";
import { Toaster } from "./components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import GlobalSEO from "./components/GlobalSEO";
import { InlineEditProvider, useInlineEdit } from "./context/InlineEditContext";
import { InlineEditToolbar } from "./components/inline/InlineEditToolbar";
import "./components/inline/inline.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Wrapper component to handle coming soon mode
const AppRoutes = () => {
  const location = useLocation();
  const [comingSoonEnabled, setComingSoonEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkComingSoon = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/coming-soon/status`);
        if (response.ok) {
          const data = await response.json();
          setComingSoonEnabled(data.enabled);
        }
      } catch (error) {
        console.error('Failed to check coming soon status:', error);
      } finally {
        setLoading(false);
      }
    };
    checkComingSoon();
  }, []);

  // Show loading state briefly
  if (loading) {
    return null;
  }

  // Check if current route is admin or speaker-invite (these should bypass coming soon)
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isSpeakerInvite = location.pathname.startsWith('/speaker-invite');
  const isLinkHub = location.pathname.startsWith('/links/');

  // Admin bypass: if the user is logged in as admin AND has ?preview=1 or ?admin_bypass=1
  // in the URL, let them see the real site instead of the Coming Soon splash. This is what
  // the "Preview" button in the admin panel appends when it opens the public page.
  const params = new URLSearchParams(location.search);
  const wantsBypass = params.get('preview') === '1' || params.get('admin_bypass') === '1';
  const isAdminLoggedIn = typeof window !== 'undefined' && !!localStorage.getItem('admin_token');
  const adminBypassComingSoon = wantsBypass && isAdminLoggedIn;

  // If coming soon is enabled and not on admin/speaker-invite/link-hub pages, show coming soon
  if (comingSoonEnabled && !isAdminRoute && !isSpeakerInvite && !isLinkHub && !adminBypassComingSoon) {
    return <ComingSoonPage />;
  }

  return (
    <>
      <GlobalSEO />
      <AdminBar />
      <InlineEditBodyClass />
      <InlineEditToolbar />
      <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/sponsorship" element={<SponsorshipPage />} />
      <Route path="/speakers" element={<SpeakersPage />} />
      <Route path="/program" element={<ProgramPage />} />
      <Route path="/golf-outing" element={<GolfOutingPage />} />
      <Route path="/speaker-invite/:token" element={<SpeakerInvitePage />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/speakers" element={<AdminSpeakers />} />
      <Route path="/admin/content" element={<AdminContent />} />
      <Route path="/admin/global-content" element={<AdminGlobalContent />} />
      <Route path="/admin/sponsorship" element={<AdminSponsorship />} />
      <Route path="/admin/program" element={<AdminProgram />} />
      <Route path="/admin/golf" element={<AdminGolf />} />
      <Route path="/admin/homepage" element={<AdminHomepage />} />
      <Route path="/admin/layout-builder" element={<AdminLayoutBuilder />} />
      <Route path="/admin/layout-builder/:layoutId" element={<AdminLayoutBuilder />} />
      <Route path="/admin/speaker-invite" element={<AdminSpeakerInvite />} />
      <Route path="/admin/settings" element={<AdminSettings />} />
      <Route path="/admin/visibility" element={<AdminVisibility />} />
      <Route path="/admin/coming-soon" element={<AdminComingSoon />} />
      <Route path="/admin/analytics" element={<AdminAnalytics />} />
      <Route path="/admin/suppression" element={<AdminSuppression />} />
      <Route path="/admin/assistant" element={<AdminAssistantPage />} />
      <Route path="/admin/email" element={<EmailCampaigns />} />
      <Route path="/admin/email/lists" element={<EmailContactLists />} />
      <Route path="/admin/email/edit/:campaignId" element={<EmailEditor />} />
      <Route path="/admin/email/review/:campaignId" element={<EmailReview />} />
      <Route path="/admin/email/logs/:campaignId" element={<EmailLogs />} />
      <Route path="/admin/email/preview/:campaignId" element={<EmailReview />} />
      <Route path="/admin/email/analytics/:campaignId" element={<EmailCampaignAnalytics />} />
      <Route path="/admin/link-hub" element={<AdminLinkHub />} />
      <Route path="/admin/link-hub/analytics" element={<AdminLinkHubAnalytics />} />
      <Route path="/links/:slug" element={<LinkHub />} />
    </Routes>
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <InlineEditProvider>
          <AppRoutes />
        </InlineEditProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
    </HelmetProvider>
  );
}

/** Small helper that toggles a body class based on inline-edit mode so the CSS selectors light up. */
const InlineEditBodyClass = () => {
  const { enabled } = useInlineEdit();
  useEffect(() => {
    if (enabled) document.body.classList.add('inline-edit-enabled');
    else document.body.classList.remove('inline-edit-enabled');
    return () => document.body.classList.remove('inline-edit-enabled');
  }, [enabled]);
  return null;
};

export default App;
